import { defineStore } from 'pinia';
import { ref } from 'vue';
import clarityService from '@/Services/clarity';

const CONSENT_KEY = 'cookie_consent';
const CONSENT_DISMISSED_KEY = 'cookie_consent_dismissed';
const REGION_KEY = 'user_region';

// Regions that require cookie consent
const CONSENT_REQUIRED_REGIONS = [
    // EU/EEA Countries (GDPR)
    'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
    'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
    'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
    // EEA
    'IS', 'LI', 'NO',
    // UK (UK GDPR)
    'GB',
    // Switzerland
    'CH',
    // Brazil (LGPD)
    'BR',
];

export const useConsentStore = defineStore('consent', () => {
    const hasConsented = ref(false);
    const isDismissed = ref(false);
    const adStorage = ref('denied');
    const analyticsStorage = ref('denied');
    const requiresConsent = ref(false);
    const regionChecked = ref(false);

    async function initialize() {
        const stored = localStorage.getItem(CONSENT_KEY);
        const dismissed = localStorage.getItem(CONSENT_DISMISSED_KEY);

        if (stored) {
            const consent = JSON.parse(stored);
            hasConsented.value = true;
            adStorage.value = consent.ad_Storage || 'denied';
            analyticsStorage.value = consent.analytics_Storage || 'denied';

            // Apply stored consent to Clarity
            clarityService.handleConsent({
                ad_Storage: adStorage.value,
                analytics_Storage: analyticsStorage.value,
            });
        }

        if (dismissed) {
            isDismissed.value = true;
        }

        // Check if user is in a region requiring consent
        await checkRegion();
    }

    async function checkRegion() {
        // Check cached region first
        const cachedRegion = localStorage.getItem(REGION_KEY);
        if (cachedRegion) {
            requiresConsent.value = CONSENT_REQUIRED_REGIONS.includes(cachedRegion);
            regionChecked.value = true;
            return;
        }

        // Fetch user's country via free geolocation API
        fetch('https://ipapi.co/country/')
            .then(response => response.text())
            .then(countryCode => {
                const code = countryCode.trim().toUpperCase();
                localStorage.setItem(REGION_KEY, code);
                requiresConsent.value = CONSENT_REQUIRED_REGIONS.includes(code);
                regionChecked.value = true;
            })
            .catch(() => {
                // On error, default to showing consent (safer)
                requiresConsent.value = true;
                regionChecked.value = true;
            });
    }

    function acceptAll() {
        hasConsented.value = true;
        adStorage.value = 'granted';
        analyticsStorage.value = 'granted';

        const consent = {
            ad_Storage: 'granted',
            analytics_Storage: 'granted',
        };

        localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
        clarityService.handleConsent(consent);
    }

    function acceptAnalyticsOnly() {
        hasConsented.value = true;
        adStorage.value = 'denied';
        analyticsStorage.value = 'granted';

        const consent = {
            ad_Storage: 'denied',
            analytics_Storage: 'granted',
        };

        localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
        clarityService.handleConsent(consent);
    }

    function denyAll() {
        hasConsented.value = true;
        adStorage.value = 'denied';
        analyticsStorage.value = 'denied';

        const consent = {
            ad_Storage: 'denied',
            analytics_Storage: 'denied',
        };

        localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
        clarityService.handleConsent(consent);
    }

    function dismiss() {
        isDismissed.value = true;
        localStorage.setItem(CONSENT_DISMISSED_KEY, 'true');
    }

    function shouldShowBanner() {
        // Only show banner if: region requires consent AND user hasn't consented/dismissed AND region check is complete
        return requiresConsent.value && !hasConsented.value && !isDismissed.value && regionChecked.value;
    }

    function clearConsent() {
        localStorage.removeItem(CONSENT_KEY);
        localStorage.removeItem(CONSENT_DISMISSED_KEY);
        hasConsented.value = false;
        isDismissed.value = false;
        adStorage.value = 'denied';
        analyticsStorage.value = 'denied';
    }

    return {
        hasConsented,
        isDismissed,
        adStorage,
        analyticsStorage,
        requiresConsent,
        regionChecked,
        initialize,
        acceptAll,
        acceptAnalyticsOnly,
        denyAll,
        dismiss,
        shouldShowBanner,
        clearConsent,
    };
});
